import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFDReferrals, useUpdateFDReferral } from '../hooks/useFoodDistribution';
import { FDReferral } from '../types/foodDistribution';
import '../styles/FoodDistribution.css';

const STATUS_OPTIONS = ['Referred', 'Engaged', 'Completed', 'Declined', 'Cancelled'];

type SortKey = 'client' | 'service' | 'referredDate' | 'status' | 'engagementDate' | 'completionDate';
type SortDir = 'asc' | 'desc';

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Referred: 'fd-badge-referred',
    Engaged: 'fd-badge-engaged',
    Completed: 'fd-badge-completed',
    Declined: 'fd-badge-declined',
    Cancelled: 'fd-badge-cancelled',
  };
  return <span className={`fd-badge ${map[status] ?? ''}`}>{status}</span>;
}

function SortHeader({
  label,
  sortKey,
  currentSort,
  currentDir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentSort: SortKey;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const isActive = currentSort === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label} {isActive ? (currentDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );
}

function ReferralRow({
  referral,
  onStatusChange,
  saving,
}: {
  referral: FDReferral;
  onStatusChange: (id: number, status: string) => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(referral.engagementStatus);

  const handleSave = () => {
    onStatusChange(referral.fdReferralId, selectedStatus);
    setEditing(false);
  };

  return (
    <tr>
      <td>
        <Link to={`/food-distribution/${referral.fdClientId}`} className="fd-table-link">
          {referral.firstName && referral.lastName
            ? `${referral.firstName} ${referral.lastName}`
            : referral.clientName ?? (referral.clientUid || `Client #${referral.fdClientId}`)}
        </Link>
      </td>
      <td>{referral.referralType}</td>
      <td>{new Date(referral.referredDate).toLocaleDateString()}</td>
      <td>
        {editing ? (
          <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{ padding: '0.25rem 0.5rem', borderRadius: 5, border: '1px solid #ddd', fontSize: '0.875rem' }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ padding: '0.25rem 0.625rem', background: '#5B2C86', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: '0.8rem' }}
            >
              {saving ? '...' : 'Save'}
            </button>
            <button
              onClick={() => setEditing(false)}
              style={{ padding: '0.25rem 0.625rem', background: '#eee', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: '0.8rem' }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <StatusBadge status={referral.engagementStatus} />
            <button
              onClick={() => setEditing(true)}
              style={{ padding: '0.2rem 0.5rem', background: 'none', border: '1px solid #ddd', borderRadius: 5, cursor: 'pointer', fontSize: '0.75rem', color: '#666' }}
            >
              Edit
            </button>
          </div>
        )}
      </td>
      <td>{referral.engagementDate ? new Date(referral.engagementDate).toLocaleDateString() : '—'}</td>
      <td>{referral.completionDate ? new Date(referral.completionDate).toLocaleDateString() : '—'}</td>
    </tr>
  );
}

export function FDReferralsPage() {
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortKey>('referredDate');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const pageSize = 25;

  const { data, isLoading, error } = useFDReferrals({
    engagementStatus: filterStatus || undefined,
    page,
    pageSize,
    sortBy,
    sortDir,
  });

  const { mutate: updateStatus, isPending: saving } = useUpdateFDReferral();

  const referrals = data?.data ?? [];
  const pagination = data?.pagination;

  const handleStatusChange = (id: number, status: string) => {
    updateStatus({ id, data: { engagementStatus: status } });
  };

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handlePendingClick = () => {
    if (filterStatus === 'Referred') {
      setFilterStatus('');
    } else {
      setFilterStatus('Referred');
    }
    setPage(1);
  };

  const pendingCount = referrals.filter((r) => r.engagementStatus === 'Referred').length;

  return (
    <div>
      <div style={{ marginBottom: '0.75rem' }}>
        <Link to="/food-distribution" className="fd-table-link">← Back to Food Distribution</Link>
      </div>

      <div className="page-header">
        <h2>Referral Management</h2>
        <p>Track and update wraparound service referral status</p>
      </div>

      {/* Filter bar */}
      <div className="fd-filter-bar" style={{ marginBottom: '1.25rem' }}>
        <label>
          Status
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        {(pendingCount > 0 || filterStatus === 'Referred') && (
          <button
            onClick={handlePendingClick}
            style={{
              marginLeft: 'auto',
              background: filterStatus === 'Referred' ? '#1565c0' : '#e3f2fd',
              color: filterStatus === 'Referred' ? '#fff' : '#1565c0',
              padding: '0.4rem 0.875rem',
              borderRadius: 6,
              fontSize: '0.875rem',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            {filterStatus === 'Referred'
              ? 'Showing pending only — click to show all'
              : `${pendingCount} referral${pendingCount !== 1 ? 's' : ''} pending follow-up`}
          </button>
        )}
      </div>

      <div className="card">
        {isLoading && <p style={{ color: '#888' }}>Loading referrals...</p>}
        {error && <p style={{ color: '#c62828' }}>Failed to load referrals.</p>}

        {!isLoading && referrals.length === 0 && (
          <div className="fd-empty">
            <div className="fd-empty-icon">🤝</div>
            <p>No referrals found{filterStatus ? ` with status "${filterStatus}"` : ''}.</p>
          </div>
        )}

        {!isLoading && referrals.length > 0 && (
          <>
            <div className="fd-table-wrap">
              <table className="fd-table">
                <thead>
                  <tr>
                    <SortHeader label="Client" sortKey="client" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Service" sortKey="service" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Referred Date" sortKey="referredDate" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Status" sortKey="status" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Engaged" sortKey="engagementDate" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                    <SortHeader label="Completed" sortKey="completionDate" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r) => (
                    <ReferralRow
                      key={r.fdReferralId}
                      referral={r}
                      onStatusChange={handleStatusChange}
                      saving={saving}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && (
              <div className="fd-pagination">
                <span className="fd-pagination-info">
                  {pagination.totalItems} referral{pagination.totalItems !== 1 ? 's' : ''}
                  {' · '}Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="fd-pagination-buttons">
                  <button onClick={() => setPage(1)} disabled={page === 1}>«</button>
                  <button onClick={() => setPage(page - 1)} disabled={page === 1}>‹ Prev</button>
                  <button onClick={() => setPage(page + 1)} disabled={page >= pagination.totalPages}>Next ›</button>
                  <button onClick={() => setPage(pagination.totalPages)} disabled={page >= pagination.totalPages}>»</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
